(function () {
  'use strict';

  /* ---------- Helpers (shared) ---------- */

  // Detect iOS (where Safari needs help keeping inputs visible)
  const IS_IOS = (() => {
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const iOSByPlatform = /iP(hone|od|ad)/.test(platform);
    const iPadOS13Plus = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    return iOSByPlatform || iPadOS13Plus || /iPhone|iPad|iPod/i.test(ua);
  })();

  function adjustPreview(cardEl, textEl) {
    if (!cardEl || !textEl) return;

    const len = (textEl.textContent || '').trim().length;
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;
    const baseFont = isDesktop ? 28 : 26;
    const minFont  = isDesktop ? 20 : 18;
    const t = Math.min(len / 220, 1);
    const fontPx = Math.round(baseFont - (baseFont - minFont) * t);
    textEl.style.fontSize = fontPx + 'px';

    const baseH = 180;
    const fudge = 14;
    const cap = Math.floor(Math.min(window.innerHeight * 0.45, 360));

    cardEl.style.height = 'auto';
    const contentH = Math.max(textEl.scrollHeight + fudge, baseH);
    const target   = Math.min(contentH, cap);
    cardEl.style.height = target + 'px';
    cardEl.style.overflow = 'hidden';
  }

  function autoGrow(el) {
    if (!el) return;
    const cap = Math.floor(Math.min(window.innerHeight * 0.5, 360));
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, cap) + 'px';
  }

  function toTitleCase(str = '') {
    return str.toLowerCase().replace(/[\p{L}][\p{L}'-]*/gu, word =>
      word.replace(/^[\p{L}]/u, m => m.toUpperCase())
    );
  }

  function collapseBlankLines(str = '') {
    return str.replace(/\n{3,}/g, '\n\n').trim();
  }

  function setFooterPadding(tray) {
    if (!tray) return;
    const field  = tray.querySelector('[data-field]') || tray.querySelector('.p-tray__field');
    const footer = tray.querySelector('.p-tray__footer');
    if (!field || !footer) return;

    const h = footer.getBoundingClientRect().height || 0;
    field.style.paddingBottom = (h + 16) + 'px';
  }

  function setViewportVars(tray) {
    if (!tray) return;

    const vv = window.visualViewport;
    if (!vv) {
      tray.style.removeProperty('--p-vvh');
      tray.style.removeProperty('--p-kb');
      return;
    }

    tray.style.setProperty('--p-vvh', vv.height + 'px');

    const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    tray.style.setProperty('--p-kb', kb + 'px');
  }

  let __hmScrollY = 0;

  function init(root) {
    if (!root) return;

    let __isOpening = false;
    let lastFocused = null;

    const formId = root.dataset.formId;
    const form   = formId ? document.getElementById(formId) : root.closest('form');

    const product  = root.dataset.productId;
    const required = root.dataset.required === 'true';
    const max      = parseInt(root.dataset.max || '250', 10);
    const prefer   = root.dataset.position || 'right';
    const autoOpen = root.dataset.autoOpen === 'true';

    const tray    = root.querySelector('[data-tray]');
    const openers = root.querySelectorAll('[data-open]');
    const closer  = root.querySelector('[data-close]');
    const saver   = root.querySelector('[data-save]');

    const input        = root.querySelector('[data-input]');
    const error        = root.querySelector('[data-error]');
    const count        = root.querySelector('[data-count]');
    const hidden       = root.querySelector('[data-storage="hidden-input"]');
    const previewWrap  = root.querySelector('.p-tray-preview');
    const previewText  = root.querySelector('[data-preview]');
    const livePreview  = root.querySelector('[data-live-preview]');
    const previewEmpty = root.querySelector('[data-preview-empty]');
    const previewCard  = root.querySelector('.p-tray__preview-card');
    const copyBtn      = root.querySelector('[data-copy]');
    const nudge        = root.querySelector('[data-nudge]');

    const triggerEmpty   = root.querySelector('[data-trigger-empty]');
    const triggerEdit    = root.querySelector('[data-trigger-edit]');
    const triggerPreview = root.querySelector('[data-trigger-preview]');
    const summary        = root.querySelector('[data-hm-summary]') ||
                           root.closest('.hm-bubble')?.querySelector('[data-hm-summary]');

    const toSelector   = '[name="properties[To]"]';
    const fromSelector = '[name="properties[From]"]';
    let toInput = null, fromInput = null;

    if (form) {
      toInput   = form.querySelector(toSelector);
      fromInput = form.querySelector(fromSelector);
      if (!toInput)   toInput   = document.querySelector(`${toSelector}[form="${form.id}"]`);
      if (!fromInput) fromInput = document.querySelector(`${fromSelector}[form="${form.id}"]`);
    }
    if (!toInput)   toInput   = document.querySelector(toSelector);
    if (!fromInput) fromInput = document.querySelector(fromSelector);

    const inkSelector = 'input[name="properties[Ink]"]';
    let inkRadios = [];
    if (form) inkRadios = [...form.querySelectorAll(inkSelector)];
    if (form) inkRadios = inkRadios.concat([...document.querySelectorAll(`${inkSelector}[form="${form.id}"]`)]);
    if (inkRadios.length === 0) inkRadios = [...document.querySelectorAll(inkSelector)];

    function currentInk() {
      const sel = inkRadios.find(r => r.checked);
      return sel ? sel.value : '';
    }

    function updateSummaryChip() {
      if (!summary) return;
      const toVal  = (toInput?.value || '').trim();
      const inkVal = currentInk();
      if (!toVal && !inkVal) { summary.hidden = true; summary.innerHTML = ''; return; }

      const chips = [];
      if (toVal) chips.push(`<span class="hm-chip hm-chip--to">To ${toTitleCase(toVal)}</span>`);
      if (inkVal) chips.push(
        `<span class="hm-chip hm-chip--ink ${inkVal}">
           <span class="hm-chip__dot" aria-hidden="true"></span>
           Ink: ${inkVal}
         </span>`
      );
      summary.innerHTML = chips.join('');
      summary.hidden = false;
    }

    function paintInk() {
      inkRadios.forEach(radio => {
        const label = radio.closest('label.hm-pill');
        if (label) label.classList.toggle('is-selected', radio.checked);
      });

      const sel = inkRadios.find(r => r.checked);
      if (sel) {
        const group = sel.closest('.hm-ink-pills');
        if (group) group.setAttribute('data-ink', sel.value);

        root.setAttribute('data-ink', sel.value);
        if (tray) tray.setAttribute('data-ink', sel.value);
      }

      if (livePreview) {
        livePreview.style.color = currentInk() === 'Blue' ? '#2563eb' : '#0f172a';
      }
      updateSummaryChip();
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => adjustPreview(previewCard, livePreview));
    }

    const addToCartBtn = form ? form.querySelector('[type="submit"].product-form__submit') : null;
    const dynCheckout  = form ? form.querySelector('.shopify-payment-button__button') : null;

    const storageKey = `personalization:${product}`;
    const supportsDialog = typeof HTMLDialogElement === 'function' && tray && typeof tray.showModal === 'function';

    if (tray && tray.parentElement !== document.body) document.body.appendChild(tray);

    const useBottom = () => prefer === 'bottom' || window.matchMedia('(max-width: 749px)').matches;
    const setMode = () => {
      const isBottom = useBottom();
      tray.classList.toggle('bottom', isBottom);
      tray.classList.toggle('right', !isBottom);
      setFooterPadding(tray);
    };

    const onVV = () => { setViewportVars(tray); setFooterPadding(tray); };

    function combinedPreviewText() {
      const toRaw   = (toInput?.value || '').trim();
      const msg     = (input?.value || '').trim();
      const fromRaw = (fromInput?.value || '').trim();

      const to   = toRaw   ? toTitleCase(toRaw)   : '';
      const from = fromRaw ? toTitleCase(fromRaw) : '';

      const lines = [];
      if (to)   lines.push(`Dear ${to},`);
      if (msg)  lines.push(msg);
      if (from) lines.push(`- ${from}`);
      return collapseBlankLines(lines.join('\n\n'));
    }

    function updatePreviewFromAll() {
      if (!livePreview || !previewEmpty) return;
      const combined = combinedPreviewText();
      livePreview.textContent = combined;
      previewEmpty.style.display = combined ? 'none' : '';
      adjustPreview(previewCard, livePreview);
      setFooterPadding(tray);
    }

    const fire = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
    const setError = (msg) => { if (!error) return; error.textContent = msg || ''; error.hidden = !msg; };

    const validate = () => {
      const val = (input?.value || '').trim();
      if (required && !val) { setError('Please enter a message.'); return false; }
      if (val.length > max) { setError('Message is too long.'); return false; }
      setError('');
      return true;
    };

    const updateCounter = () => { if (count && input) count.textContent = String((input.value || '').length); };
    const syncHidden    = () => { if (hidden && input) hidden.value = (input.value || '').trim(); };

    const updateCompactPreview = () => {
      if (!previewText || !input) return;
      const v = (input.value || '').trim();
      previewText.textContent = v;
      if (previewWrap) previewWrap.hidden = !v;

      const has = v.length > 0;
      root.classList.toggle('has-message', has);
      if (triggerEmpty) triggerEmpty.hidden = has;
      if (triggerEdit)  triggerEdit.hidden  = !has;

      if (triggerPreview) {
        triggerPreview.textContent = has ? (v.length > 70 ? v.slice(0, 70) + '…' : v) : '';
        triggerPreview.hidden = !has;
      }
    };

    const restoreDraft = () => { try { const v = localStorage.getItem(storageKey); if (v && input && !input.value) input.value = v; } catch (_) {} };
    const saveDraft    = () => { try { if (input) localStorage.setItem(storageKey, input.value || ''); } catch (_) {} };

    function open() {
      __hmScrollY = window.scrollY || document.documentElement.scrollTop || 0;
      document.documentElement.classList.add('hm-lock');
      document.body.classList.add('hm-lock');
      document.documentElement.style.top = `-${__hmScrollY}px`;

      lastFocused = document.activeElement;
      setMode();

      if (supportsDialog) { if (!tray.open) tray.showModal(); }
      else { tray.setAttribute('open', ''); }
      tray.classList.add('is-open');

      onVV();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', onVV, { passive: true });
        window.visualViewport.addEventListener('scroll', onVV, { passive: true });
      }
      window.addEventListener('orientationchange', onVV, { passive: true });

      // Prevent open-jump
      __isOpening = true;
      tray.setAttribute('tabindex', '-1');
      try { tray.focus({ preventScroll: true }); } catch (_) { tray.focus(); }

      const field = tray.querySelector('[data-field]') || tray.querySelector('.p-tray__field');
      const resetScroll = () => { if (field) field.scrollTop = 0; };

      resetScroll();
      requestAnimationFrame(() => {
        resetScroll();
        requestAnimationFrame(resetScroll);
      });
      setTimeout(resetScroll, 120);

      setFooterPadding(tray);
      setTimeout(onVV, 250);
      setTimeout(() => { __isOpening = false; }, 400);

      fire('tray_open', { productId: product });
    }

    function close() {
      setError('');
      tray.classList.remove('is-open');

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onVV);
        window.visualViewport.removeEventListener('scroll', onVV);
      }
      window.removeEventListener('orientationchange', onVV);
      tray.style.removeProperty('--p-vvh');
      tray.style.removeProperty('--p-kb');

      if (supportsDialog) { try { tray.close(); } catch (_) {} }
      else { tray.removeAttribute('open'); }

      document.documentElement.classList.remove('hm-lock');
      document.body.classList.remove('hm-lock');
      document.documentElement.style.top = '';
      window.scrollTo(0, __hmScrollY);

      if (lastFocused) lastFocused.focus();
      fire('tray_close', { productId: product });
      updateCompactPreview();
    }

    tray.addEventListener('click', (e) => { if (e.target === tray) close(); });
    tray.addEventListener('cancel', (e) => { e.preventDefault(); close(); });

    // ✅ IMPORTANT CHANGE:
    // Only do scrollIntoView helper on iOS. Android Chrome shouldn't do this.
    tray.addEventListener('focusin', (e) => {
      if (__isOpening) return;
      if (!IS_IOS) return;

      const el = e.target;
      if (!el || !el.matches('textarea, input, select')) return;

      setTimeout(() => {
        onVV();

        const vv = window.visualViewport;
        if (!vv) return;
        const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        if (kb < 80) return;

        try {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (_) {
          el.scrollIntoView(true);
        }
      }, 250);
    }, { passive: true });

    openers.forEach(b => b.addEventListener('click', open));
    if (closer) closer.addEventListener('click', close);
    if (saver)  saver.addEventListener('click', () => { close(); });

    if (input) {
      input.addEventListener('input', () => {
        updateCounter();
        syncHidden();
        saveDraft();
        updatePreviewFromAll();
        updateCompactPreview();
        autoGrow(input);
        onVV();
        fire('message_input', { length: (input.value || '').length });
      });
    }

    if (toInput)   toInput.addEventListener('input', () => { updatePreviewFromAll(); updateSummaryChip(); });
    if (fromInput) fromInput.addEventListener('input', updatePreviewFromAll);

    inkRadios.forEach(r => r.addEventListener('change', paintInk));
    paintInk();
    updateSummaryChip();

    if (copyBtn) {
      copyBtn.addEventListener('click', async () => {
        const txt = (input?.value || '').trim();
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(txt);
          } else {
            const ta = document.createElement('textarea');
            ta.value = txt; document.body.appendChild(ta);
            ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
          }
          copyBtn.textContent = 'Copied!';
          setTimeout(() => (copyBtn.textContent = 'Copy message'), 1000);
        } catch (_) {
          copyBtn.textContent = 'Copy failed';
          setTimeout(() => (copyBtn.textContent = 'Copy message'), 1200);
        }
      });
    }

    const gate = (e) => {
      if (required && !validate()) {
        e.preventDefault();
        e.stopPropagation();
        showGentleReminder();
        setTimeout(open, 600); // Open tray after showing reminder
      }
    };

    function showGentleReminder() {
      // Find or create reminder element
      let reminder = root.querySelector('[data-reminder]');
      if (!reminder) {
        reminder = document.createElement('div');
        reminder.setAttribute('data-reminder', '');
        reminder.className = 'p-tray-reminder';
        reminder.innerHTML = '✍️ Please write your personalized message first';

        // Insert after the trigger button
        const trigger = root.querySelector('.p-tray-trigger');
        if (trigger && trigger.parentNode) {
          trigger.parentNode.insertBefore(reminder, trigger.nextSibling);
        }
      }

      // Show with animation
      reminder.classList.remove('is-hiding');
      reminder.classList.add('is-visible');

      // Gentle shake on the button
      const trigger = root.querySelector('.p-tray-trigger');
      if (trigger) {
        trigger.classList.add('shake-gentle');
        setTimeout(() => trigger.classList.remove('shake-gentle'), 500);
      }

      // Auto-hide after 5 seconds
      setTimeout(() => {
        reminder.classList.add('is-hiding');
        setTimeout(() => reminder.classList.remove('is-visible', 'is-hiding'), 300);
      }, 5000);
    }

    if (form)        form.addEventListener('submit', gate, true);
    if (dynCheckout) dynCheckout.addEventListener('click', gate, true);

    setMode();
    restoreDraft(); updateCounter(); syncHidden();
    updatePreviewFromAll(); updateCompactPreview();
    autoGrow(input);
    setFooterPadding(tray);
    onVV();
    if (autoOpen) open();

    window.addEventListener('resize', () => {
      setMode();
      adjustPreview(previewCard, livePreview);
      autoGrow(input);
      setFooterPadding(tray);
      onVV();
    });

    if (form && addToCartBtn) {
      form.addEventListener('submit', () => {
        const len = (input?.value || '').length;
        fire('add_to_cart_with_message', { length: len });
      }, { capture: true });
    }
  }

  document.querySelectorAll('.p-tray-root').forEach(init);
})();
